import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  Ruler,
  Edit,
  ArrowUpDown,
  Search,
  Filter,
  Plus,
  CheckCircle,
  XCircle as XCircleIcon,
  X,
  AlertTriangle,
  Eye,
  ClipboardList,
  User as UserIcon,
  Tag,
} from "lucide-react";
import { UoMMaster, User } from "../../../services/uomMasterService";
import Select from "../../../shared/components/form/Select";
import api from "../../../services/axiosInstance";
import { BRAND_COLORS } from "../../../shared/constants/brandColors";

// UoM Filter Component
type OptionType = { label: string; value: string };

import { getCategoryNames } from "../constants/uomConstants";

interface UoMMasterFilterProps {
  category: string;
  setCategory: (val: string) => void;
  isActive: boolean | null;
  setIsActive: (val: boolean | null) => void;
}

const UoMMasterFilter = ({
  category,
  setCategory,
  isActive,
  setIsActive,
}: UoMMasterFilterProps) => {
  const categoryOptions: OptionType[] = [
    { value: "", label: "All Categories" },
    ...getCategoryNames().map(cat => ({ value: cat, label: cat }))
  ];

  const statusOptions: OptionType[] = [
    { value: "", label: "All Status" },
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ];

  const handleResetFilters = () => {
    setCategory("");
    setIsActive(null);
  };

  const hasActiveFilters = category !== "" || isActive !== null;

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
      {/* Category Filter */}
      <div className="flex-1 min-w-[180px]">
        <Select
          options={categoryOptions}
          value={categoryOptions.find((opt) => opt.value === category)}
          placeholder="Filter by Category"
          onChange={(selectedOption) => {
            setCategory(selectedOption?.value || "");
          }}
        />
      </div>

      {/* Status Filter */}
      <div className="flex-1 min-w-[180px]">
        <Select
          options={statusOptions}
          value={statusOptions.find((opt) => opt.value === (isActive === null ? "" : String(isActive)))}
          placeholder="Filter by Status"
          onChange={(selectedOption) => {
            if (selectedOption?.value === "") {
              setIsActive(null);
            } else {
              setIsActive(selectedOption?.value === "true");
            }
          }}
        />
      </div>

      {/* Reset Filters Button */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={handleResetFilters}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-700 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
        >
          <X size={14} />
          Clear
        </button>
      )}
    </div>
  );
};

// Enhanced ToggleStatusCell Component
interface ToggleStatusCellProps {
  uomMaster: UoMMaster;
  onRefresh: () => void;
}

const ToggleStatusCell: React.FC<ToggleStatusCellProps> = ({ uomMaster, onRefresh }) => {
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      const endpoint = uomMaster.isActive ? "inactivate" : "activate";
      const response = await api.post(
        `/uom-masters/${uomMaster._id}/${endpoint}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const updatedIsActive = response?.data?.data?.isActive ?? !uomMaster.isActive;

      toast.success(`UOM ${updatedIsActive ? "activated" : "inactivated"} successfully`);
      setConfirmOpen(false);
      onRefresh();
    } catch (err: any) {
      console.error("Toggle error:", err);
      toast.error("Failed to toggle UOM status");
    } finally {
      setIsLoading(false);
    }
  };

  const isActive = uomMaster.isActive;

  const handleOpenModal = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setClickPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpenModal}
        title={`Click to ${isActive ? "deactivate" : "activate"}`}
        className={`relative inline-flex items-center justify-center w-10 h-5 rounded-full border transition-all duration-300 select-none ${isActive
          ? "bg-green-50 border-green-200 hover:bg-green-100"
          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
          }`}
      >
        <div
          className={`w-3 h-3 rounded-full transform transition-all duration-300 ${isActive
            ? "translate-x-2 bg-green-500"
            : "-translate-x-2 bg-gray-400"
            }`}
        />
      </button>

      <AnimatePresence>
        {isConfirmOpen && (
          <div className="fixed inset-0 z-90 overflow-y-auto bg-gray-500/5">
            <div className="flex min-h-full items-center justify-end p-4">
              <motion.div
                initial={{
                  opacity: 0,
                  scale: 0.95,
                  x: clickPosition.x,
                  y: clickPosition.y,
                  z: -100
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  x: -20,
                  y: 0,
                  z: 0
                }}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                  x: clickPosition.x,
                  y: clickPosition.y,
                  z: -100
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="relative w-full max-w-md bg-white rounded-lg border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.12)] mr-4"
              >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${isActive ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"
                      }`}>
                      {isActive ? (
                        <XCircleIcon size={20} className="text-red-500" />
                      ) : (
                        <CheckCircle size={20} className="text-green-500" />
                      )}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {isActive ? "Inactivate UOM" : "Activate UOM"}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {uomMaster.name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setConfirmOpen(false)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-gray-200 transition-all duration-200"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-50 rounded-lg flex items-center justify-center border border-amber-200">
                      <AlertTriangle size={14} className="text-amber-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                        Are you sure you want to{" "}
                        <span className="font-medium text-gray-900">
                          {isActive ? "inactivate" : "activate"}
                        </span>{" "}
                        <span className="font-medium" style={{ color: BRAND_COLORS.primary }}>{uomMaster.name}</span>?
                      </p>
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {isActive
                            ? "This UOM will be marked as inactive and won't appear in active lists."
                            : "This UOM will be reactivated and will appear in lists again."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                  <button
                    onClick={() => setConfirmOpen(false)}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleToggle}
                    disabled={isLoading}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md transition-all duration-200 disabled:opacity-50 ${isActive
                      ? "bg-red-500 hover:bg-red-600 focus:ring-red-500"
                      : "bg-green-500 hover:bg-green-600 focus:ring-green-500"
                      } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <span>
                        {isActive ? "Inactivate" : "Activate"}
                      </span>
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

interface Props {
  uomMasters: UoMMaster[];
  search: string;
  setSearch: (val: string) => void;
  category: string;
  setCategory: (val: string) => void;
  isActive: boolean | null;
  setIsActive: (val: boolean | null) => void;
  page: number;
  setPage: (val: number) => void;
  totalUoMMasters: number;
  onEdit?: (uomMaster: UoMMaster) => void;
  onView?: (uomMaster: UoMMaster) => void;
  onAdd?: () => void;
  onToggleStatus?: (uomMaster: UoMMaster) => void;
  onRefresh: () => void;
  sortField: string;
  sortOrder: "asc" | "desc";
  setSortField: (field: string) => void;
  setSortOrder: (order: "asc" | "desc") => void;
  loading?: boolean;
}

const ITEMS_PER_PAGE = 6;

export default function UoMMasterTable({
  uomMasters,
  search,
  setSearch,
  category,
  setCategory,
  isActive,
  setIsActive,
  page,
  setPage,
  totalUoMMasters,
  onEdit,
  onView,
  onAdd,
  onRefresh,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  loading = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [sortMenuOpen, setSortMenuOpen] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const getUserName = (user: string | User) => {
    if (typeof user === 'object' && user !== null && 'name' in user) {
      return user.name;
    }
    if (typeof user === 'string') {
      return 'N/A';
    }
    return 'N/A';
  };

  // Keyboard shortcut for search
  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const totalPages = Math.ceil(totalUoMMasters / ITEMS_PER_PAGE);

  const handleSortOption = (field: string, order: "asc" | "desc" | "clear") => {
    if (order === "clear") {
      setSortField("createdAt");
      setSortOrder("desc");
    } else {
      setSortField(field);
      setSortOrder(order);
    }
    setSortMenuOpen(null);
  };


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
    >
      {/* Controls Section */}
      <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-white to-gray-25">
        {/* Main Controls Row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left Side: Search and Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search UOM... (Ctrl+K)"
                className="block w-full sm:w-80 pl-9 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white"
                style={{
                  '--tw-ring-color': BRAND_COLORS.primary,
                  'focusBorderColor': BRAND_COLORS.primary
                } as React.CSSProperties}
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 border ${showFilters
                ? `text-white border-transparent`
                : "text-gray-600 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              style={showFilters ? {
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
              } : {}}
            >
              <Filter size={16} />
              Filters
            </button>
          </div>

          {/* Right Side: Sort and Add */}
          <div className="flex items-center gap-3">
            {/* Sort Button */}
            <div className="relative">
              <button
                onClick={() => setSortMenuOpen(sortMenuOpen ? null : "general")}
                className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 transition-all duration-200"
                style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
              >
                <ArrowUpDown size={16} />
                Sort
              </button>

              {/* Sort Dropdown */}
              <AnimatePresence>
                {sortMenuOpen === "general" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-2 z-20 w-48 bg-white border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="p-3">
                      <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Unit Name</div>
                      <button
                        onClick={() => handleSortOption("name", "asc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "name" && sortOrder === "asc"
                          ? `font-medium bg-opacity-10`
                          : "text-gray-700"
                          }`}
                        style={sortField === "name" && sortOrder === "asc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        A → Z
                      </button>
                      <button
                        onClick={() => handleSortOption("name", "desc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "name" && sortOrder === "desc"
                          ? `font-medium bg-opacity-10`
                          : "text-gray-700"
                          }`}
                        style={sortField === "name" && sortOrder === "desc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Z → A
                      </button>

                      <div className="border-t border-gray-200 my-2"></div>
                      <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Date</div>
                      <button
                        onClick={() => handleSortOption("createdAt", "desc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "createdAt" && sortOrder === "desc"
                          ? `font-medium bg-opacity-10`
                          : "text-gray-700"
                          }`}
                        style={sortField === "createdAt" && sortOrder === "desc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Newest First
                      </button>
                      <button
                        onClick={() => handleSortOption("createdAt", "asc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "createdAt" && sortOrder === "asc"
                          ? `font-medium bg-opacity-10`
                          : "text-gray-700"
                          }`}
                        style={sortField === "createdAt" && sortOrder === "asc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Oldest First
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Add UOM Button */}
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-emerald-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-emerald-200"
              style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                '--tw-ring-color': BRAND_COLORS.primary
              } as React.CSSProperties}
            >
              <div className="relative">
                <Plus size={16} className="absolute animate-ping opacity-40" />
                <Plus size={16} className="relative" />
              </div>
              <span>Add UOM</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-4"
            >
                <UoMMasterFilter
                  category={category}
                  setCategory={setCategory}
                  isActive={isActive}
                  setIsActive={setIsActive}
                />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND_COLORS.primary }}></div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                {[
                  { label: "Name", field: "name", icon: <Ruler size={14} /> },
                  { label: "Symbol", field: "symbol", icon: <Tag size={14} /> },
                  { label: "Category", field: "category", icon: <ClipboardList size={14} /> },
                  { label: "Created By", field: "createdBy", icon: <UserIcon size={14} /> },
                ].map(({ label, field, icon }) => (
                  <th
                    key={field}
                    className="p-4 text-left text-sm font-medium text-gray-700 tracking-wide"
                  >
                    <div className="flex items-center gap-2">
                      {icon}
                      <span>{label}</span>
                    </div>
                  </th>
                ))}
                <th className="p-4 text-center text-sm font-medium text-gray-700 tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              <AnimatePresence>
                {uomMasters.map((uomMaster, index) => {
                  return (
                    <motion.tr
                      key={uomMaster._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className={`transition-all duration-200 ${!uomMaster.isActive
                        ? "bg-gray-50/50 opacity-60 hover:bg-gray-100/60"
                        : "hover:bg-gradient-to-r hover:from-gray-25 hover:to-gray-50"
                        }`}
                    >
                      {/* Name */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-300">
                              <Ruler size={16} className="text-gray-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm font-medium truncate ${!uomMaster.isActive ? "text-gray-500 line-through" : "text-gray-900"
                              }`}>
                              {uomMaster.name}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Symbol */}
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Tag size={14} className={!uomMaster.isActive ? "text-gray-300" : "text-gray-400"} />
                          <span className={`text-sm font-medium ${!uomMaster.isActive ? "text-gray-500" : "text-gray-900"}`}>
                            {uomMaster.symbol}
                          </span>
                        </div>
                      </td>

                      {/* Category */}
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <ClipboardList size={14} className={!uomMaster.isActive ? "text-gray-300" : "text-gray-400"} />
                          <span className={`text-sm font-medium ${!uomMaster.isActive ? "text-gray-500" : "text-gray-900"}`}>
                            {uomMaster.category}
                          </span>
                        </div>
                      </td>

                      {/* Created By */}
                      <td className="p-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-1">
                            <UserIcon size={14} className={!uomMaster.isActive ? "text-gray-300" : "text-gray-400"} />
                            <span className={`text-sm font-medium ${!uomMaster.isActive ? "text-gray-500" : "text-gray-900"}`}>
                              {getUserName(uomMaster.createdBy)}
                            </span>
                          </div>
                          {uomMaster.createdAt && (
                            <>
                              <span className={`text-xs ${!uomMaster.isActive ? "text-gray-400" : "text-gray-500"}`}>
                                {new Date(uomMaster.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              <span className={`text-xs ${!uomMaster.isActive ? "text-gray-400" : "text-gray-400"}`}>
                                {new Date(uomMaster.createdAt).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => onView?.(uomMaster)}
                            title="View UOM Details"
                            className={`transition-colors duration-200 ${!uomMaster.isActive
                              ? "text-gray-300 hover:text-gray-400"
                              : "text-gray-400 hover:text-blue-400"
                              }`}
                          >
                            <Eye size={16} />
                          </button>

                          <button
                            onClick={() => onEdit?.(uomMaster)}
                            title="Edit UOM"
                            className={`transition-colors duration-200 ${!uomMaster.isActive
                              ? "text-gray-300 hover:text-gray-400"
                              : "text-gray-400 hover:text-emerald-400"
                              }`}
                          >
                            <Edit size={16} />
                          </button>

                          <ToggleStatusCell uomMaster={uomMaster} onRefresh={onRefresh} />
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && uomMasters.length === 0 && (
        <div className="p-16 text-center">
          <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6 border border-gray-200"
            style={{ background: `linear-gradient(135deg, ${BRAND_COLORS.primary}20, ${BRAND_COLORS.primary}40)` }}
          >
            <Ruler size={24} className="text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">No UOM found</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
            {search || isActive !== null
              ? "Try adjusting your search or filter criteria to find the UOMs you're looking for"
              : "Get started by adding your first UOM to the system"}
          </p>
          {!search && isActive === null && (
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                '--tw-ring-color': BRAND_COLORS.primary
              } as React.CSSProperties}
            >
              <Plus size={16} />
              Add First UOM
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && uomMasters.length > 0 && (
        <div className="px-5 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-25 to-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600 font-medium">
              Showing <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min((page - 1) * ITEMS_PER_PAGE + 1, totalUoMMasters)}</span> to{" "}
              <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min(page * ITEMS_PER_PAGE, totalUoMMasters)}</span> of{" "}
              <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{totalUoMMasters}</span> UOM
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(page - 1, 1))}
                disabled={page === 1}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-md transition-all duration-200 ${page === pageNum
                        ? "text-white border border-transparent"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                        }`}
                      style={page === pageNum ? {
                        background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
                      } : {}}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setPage(Math.min(page + 1, totalPages))}
                disabled={page === totalPages}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
